import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Table } from 'antd';
import { usePriceSimulatorV3Context } from 'contexts/PriceSimulatorV3Context';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig, getAuthUserMail } from 'utils/helperMethods';
import { isPpaAnalyticsEnabled } from 'utils/ppaHelpers';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import PriceSimulatorCreateEditModal from 'components/molecules/PPA/Modals/PriceSimulatorCreateEditModal';
import { PpaScenarioActionType, PpaTableRowType } from 'types/ppa';
import { useProjectColumns } from './columns/buildProjectColumns';
import { useScenarioColumns } from './columns/buildScenarioColumns';
import { useProjectScenarios } from './hooks/useProjectScenarios';
import { useScenarioActions } from './hooks/useScenarioActions';
import { useScenarioPermissions } from './hooks/useScenarioPermissions';
import ExpandedScenarioRow from './rows/ExpandedScenarioRow';
import { StyledTableWrapper } from './ProjectScenarioExpandableTable.styles';
import type {
    InternalProjectRecord,
    ProjectRow,
    ProjectScenarioExpandableTableProps,
    ScenarioRow,
} from './types';

export type { ProjectRow, ScenarioRow, ScenarioManagerColumn } from './types';

const ProjectScenarioExpandableTable = ({
    columnsConfig,
    onScenarioClick,
    onAction,
}: ProjectScenarioExpandableTableProps) => {
    const { appConfig } = useContext(UserContext);
    const pricingSimulatorConfig = getConfig('pricingSimulator', appConfig);
    const {
        projects,
        isProjectsLoading,
        isProjectsPending,
        isProjectsRefetching,
        isFetchingNextProjectsPage,
        hasNextProjectsPage,
        fetchNextProjectsPage,
        projectSort,
        setProjectSort,
        scenarioManagerMeta,
        scenariosByProject,
        myScenarioOnly,
    } = usePriceSimulatorV3Context();

    const authUserMail = getAuthUserMail();
    const [expandedProjectIds, setExpandedProjectIds] = useState<React.Key[]>([]);
    const previousMyScenarioOnlyRef = useRef(myScenarioOnly);
    const childLimit = scenarioManagerMeta?.child_limit;

    const {
        scenariosByProjectMerged,
        scenarioCountByProject,
        loadingScenarioProjectIds,
        fetchScenariosByProject,
        refreshScenariosForProject,
        removeProjectFromScenarioState,
        removeScenarioFromProjectState,
    } = useProjectScenarios({
        scenariosByProjectProp: scenariosByProject,
        myScenarioOnly,
        userId: authUserMail || undefined,
    });

    const { canAccessAction } = useScenarioPermissions({ projects });

    const handleExpandedProjectRemoved = useCallback((projectId: number) => {
        setExpandedProjectIds((previous) =>
            previous.filter((rowKey) => String(rowKey) !== String(projectId)),
        );
    }, []);

    const actionColumn = columnsConfig.find((column) => column.id === 'action');
    const actionOptions = actionColumn?.options?.items ?? [];

    const {
        scenarioModal,
        setScenarioModal,
        openScenarioModal,
        handleRowAction,
        getActionMenuItems,
    } = useScenarioActions({
        projects,
        childLimit,
        scenariosByProjectMerged,
        scenarioCountByProject,
        actionOptions,
        removeProjectFromScenarioState,
        removeScenarioFromProjectState,
        onExpandedProjectRemoved: handleExpandedProjectRemoved,
        canAccessAction,
        onAction,
    });

    const isAnalyticsEnabled = useMemo(
        () => isPpaAnalyticsEnabled(scenarioManagerMeta, pricingSimulatorConfig),
        [scenarioManagerMeta, pricingSimulatorConfig],
    );

    const isProjectExpanded = useCallback(
        (projectKey: string) => expandedProjectIds.includes(projectKey),
        [expandedProjectIds],
    );

    useEffect(() => {
        if (previousMyScenarioOnlyRef.current === myScenarioOnly) {
            return;
        }
        previousMyScenarioOnlyRef.current = myScenarioOnly;

        expandedProjectIds.forEach((rowKey) => {
            const matchedProject = projects.find(
                (project) => String(project.project_id) === String(rowKey),
            );
            if (matchedProject) {
                void refreshScenariosForProject(matchedProject.project_id);
            }
        });
    }, [myScenarioOnly, expandedProjectIds, projects, refreshScenariosForProject]);

    const onToggleExpansion = useCallback(
        (projectKey: string, projectId: number) => {
            setExpandedProjectIds((previous) => {
                const isExpanded = previous.includes(projectKey);
                if (!isExpanded) {
                    void fetchScenariosByProject(projectId);
                    return [...previous, projectKey];
                }
                return previous.filter((id) => id !== projectKey);
            });
        },
        [fetchScenariosByProject],
    );

    const onProjectSort = useCallback(
        (columnId: string, direction: 'asc' | 'desc') => {
            setProjectSort({
                id_type: 'column',
                id: columnId,
                direction,
            });
        },
        [setProjectSort],
    );

    const columnRendererContext = useMemo(
        () => ({
            isProjectExpanded,
            showAnalyticsCheckbox: isAnalyticsEnabled,
            onToggleExpansion,
            onScenarioClick,
            getActionMenuItems,
            handleRowAction,
        }),
        [
            getActionMenuItems,
            handleRowAction,
            isAnalyticsEnabled,
            isProjectExpanded,
            onScenarioClick,
            onToggleExpansion,
        ],
    );

    const parentColumns = useProjectColumns({
        columnsConfig,
        projectSort,
        onProjectSort,
        ...columnRendererContext,
    });

    const scenarioColumns = useScenarioColumns({
        columnsConfig,
        ...columnRendererContext,
    });

    const tableData: InternalProjectRecord[] = useMemo(
        () =>
            projects.map((project) => ({
                ...project,
                key: String(project.project_id),
                row_type: PpaTableRowType.Project,
                is_private: project?.is_private ?? false,
            })),
        [projects],
    );

    const isInitialProjectsLoading = (isProjectsLoading || isProjectsPending) && tableData.length === 0;
    const isProjectsTableLoading = isInitialProjectsLoading || isProjectsRefetching;

    const expandedRowRender = useCallback(
        (projectRecord: InternalProjectRecord) => {
            const isLoading = loadingScenarioProjectIds[projectRecord.project_id];
            const scenarios = scenariosByProjectMerged[projectRecord.project_id] ?? [];
            const scenarioCount =
                scenarioCountByProject[projectRecord.project_id] ?? scenarios.length;

            return (
                <ExpandedScenarioRow
                    projectRecord={projectRecord}
                    scenarios={scenarios}
                    isLoading={Boolean(isLoading)}
                    scenarioCount={scenarioCount}
                    childLimit={childLimit}
                    scenarioColumns={scenarioColumns}
                    onCreateScenario={openScenarioModal}
                />
            );
        },
        [
            childLimit,
            loadingScenarioProjectIds,
            openScenarioModal,
            scenarioColumns,
            scenarioCountByProject,
            scenariosByProjectMerged,
        ],
    );

    const scenarioModalExistingScenarios =
        scenarioModal != null ? scenariosByProjectMerged[scenarioModal.projectId] ?? [] : [];

    return (
        <StyledTableWrapper className="mt-3">
            <PriceSimulatorCreateEditModal
                open={scenarioModal != null}
                formKind="scenario"
                projectId={scenarioModal?.projectId}
                scenarioActionType={scenarioModal?.actionType ?? PpaScenarioActionType.Create}
                editParams={scenarioModal?.editParams}
                existingScenarios={scenarioModalExistingScenarios}
                onCancel={() => setScenarioModal(null)}
                onSubmitFinish={async () => {
                    const projectId = scenarioModal?.projectId;
                    setScenarioModal(null);
                    if (projectId != null) {
                        await refreshScenariosForProject(projectId);
                    }
                }}
            />
            <InfiniteScroller
                data={tableData}
                loading={isFetchingNextProjectsPage}
                hasMore={hasNextProjectsPage}
                height={555}
                onNext={() => {
                    if (!hasNextProjectsPage || isFetchingNextProjectsPage) {
                        return;
                    }
                    void fetchNextProjectsPage();
                }}
            >
                <Table<InternalProjectRecord>
                    rowKey="key"
                    pagination={false}
                    loading={isProjectsTableLoading}
                    dataSource={tableData}
                    columns={parentColumns}
                    tableLayout="fixed"
                    expandable={{
                        expandedRowRender,
                        expandedRowKeys: expandedProjectIds,
                        onExpandedRowsChange: (rows) => {
                            setExpandedProjectIds((previous) => {
                                const nextRows = [...rows];
                                const newlyExpandedRows = nextRows.filter(
                                    (rowKey) => !previous.includes(rowKey),
                                );
                                newlyExpandedRows.forEach((rowKey) => {
                                    const matchedProject = projects.find(
                                        (project) => String(project.project_id) === String(rowKey),
                                    );
                                    if (matchedProject) {
                                        void fetchScenariosByProject(matchedProject.project_id);
                                    }
                                });
                                return nextRows;
                            });
                        },
                        expandIcon: () => null,
                        showExpandColumn: false,
                        indentSize: 0,
                    }}
                    className="price-simulator-v3-project-table"
                />
            </InfiniteScroller>
        </StyledTableWrapper>
    );
};

export default ProjectScenarioExpandableTable;
